import { createStore } from 'vuex';
import Utils from '../utils';

import trimInfo from './trim-editor/trimInfo';
import editor from './trim-editor/editor';
import contextMenu from './trim-editor/contextMenu';
import line from './trim-editor/line';
import angles from './trim-editor/angles';
import paint from './trim-editor/paint';
import undoRedo from './trim-editor/undoRedo';
import editPopup from './trim-editor/editPopup';
import inputCell from './trim-editor/inputCell';

import saveSettings from './saveSettings';
import sockets from './sockets';
import exports from './exports';
import trims from './trims';
import authentication from './authentication';
import users from './users';

import pagination from './pagination';

const store = createStore({
    modules: {
        trimInfo,
        editor,
        contextMenu,
        line,
        angles,
        paint,
        undoRedo,
        editPopup,
        inputCell,
        saveSettings,
        sockets,
        exports,
        trims,
        authentication,
        users,
        pagination,
    },

    plugins: [
        // Watch for customer context changes and clear trims
        (store) => {
            let previousCustomerId = null;
            
            store.subscribe((mutation, state) => {
                // Watch for customer ID changes in authentication
                if (mutation.type === 'authentication/setCustomerId' || 
                    mutation.type === 'authentication/clearCustomerId') {
                    
                    const currentCustomerId = state.authentication.customerId;
                    
                    // If customer context has changed, clear trims
                    if (previousCustomerId !== currentCustomerId) {
                        console.log(`🔄 [Customer Context] Changed from ${previousCustomerId} to ${currentCustomerId} - clearing trims`);
                        store.dispatch('trims/clearTrimsOnCustomerChange');
                        previousCustomerId = currentCustomerId;
                    }
                }
            });
        }
    ],

    state: {
        publicPath: process.env.BASE_URL,
        appVersion: process.env.VUE_APP_VERSION,
    },

    getters: {


    },

    mutations: {


    },

    actions: {
        async baseInitialize({commit, dispatch}) {

            // Try to restore existing handoff session, but don't fail if none exists
            // New handoff flows will establish session via HandoffView component
            try {
                const session = await dispatch('authentication/restoreHandoffSession');
                if (session) {
                    console.log('Valid handoff session restored successfully');
                } else {
                    console.log('No existing handoff session found - waiting for authentication');
                    // This is normal for new handoff flows - session will be established via HandoffView
                }
            } catch (error) {
                console.log('Session restoration error (non-fatal):', error.message);
                // Don't prevent app initialization - let route guards handle authentication
            }

            // use for the correct rendering of the infoPlaceholder icons
            document.fonts.onloadingdone = () => {
                commit('editor/setIsFontsLoaded');
            };

            // display infoPlaceholder anyway for the Safari
            let isSafari = Utils.detectSafariBrowser();

            if (isSafari) {
                setTimeout(() => {
                    commit('editor/setIsFontsLoaded');
                }, 3000);
            }

            // if the user has left the page
            window.onunload = function() {

                // inform the server about the current trim close
                dispatch('trims/updateOpenedTrimStatus', false);
            };

            // initialize the palette colors
            await dispatch('paint/initPalette');
            
            // Set up initial customer context tracking
            const customerId = store.state.authentication.customerId;
            if (customerId) {
                console.log(`🏢 [Initial Customer Context] Setting up for customer ${customerId}`);
            }
        },

        downloadExportFiles({ dispatch }, downloadData) {
            const { filesName, fileImage } = downloadData;
            
            // Only download PNG file (JSON is saved to server/database)
            if (fileImage) {
                dispatch('downloadPNGFile', { filesName, fileImage });
            }
        },

        downloadPNGFile(context, { filesName, fileImage }) {
            // Convert data URL to blob
            const imageData = fileImage.split(',')[1];
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const imageBlob = new Blob([byteArray], { type: 'image/png' });
            
            const imageUrl = URL.createObjectURL(imageBlob);
            const imageLink = document.createElement('a');
            imageLink.href = imageUrl;
            imageLink.download = `${filesName}.png`;
            document.body.appendChild(imageLink);
            imageLink.click();
            document.body.removeChild(imageLink);
            URL.revokeObjectURL(imageUrl);
        },

    }
});

export default store;